import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/leaks";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "最新爆料", to: "/feed" },
  { label: "关于我们", to: "/about" },
  { label: "联系投稿", to: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setTopicOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/feed?q=${encodeURIComponent(value)}` : "/feed");
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-6 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              蝌蚪爆料
            </span>
            <span className="mt-0.5 text-[10px] tracking-widest text-foreground-500">
              KELOU · LIVE
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
          {navLinks.slice(0, 2).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setTopicOpen(true)}
            onMouseLeave={() => setTopicOpen(false)}
          >
            <button
              type="button"
              onClick={() => setTopicOpen((prev) => !prev)}
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              主题分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            {topicOpen ? (
              <div className="absolute left-0 top-full w-64 pt-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/topic/${cat.slug}`}
                      className="flex items-start gap-3 rounded-md px-3 py-2 transition-colors hover:bg-background-100"
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${cat.icon} text-base`} />
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground-900">
                          {cat.name}
                        </span>
                        <span className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
                          {cat.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          {navLinks.slice(2).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1 md:flex"
          role="search"
        >
          <i className="ri-search-line text-sm text-foreground-500" />
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索爆料关键词"
            aria-label="搜索爆料关键词"
            className="w-32 bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-44"
          />
          <button
            type="submit"
            className="flex h-8 items-center rounded-full bg-primary-500 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <Link
          to="/contact"
          className="ml-auto hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:ml-0 md:inline-flex"
        >
          我要爆料
        </Link>

        <button
          type="button"
          aria-label="打开菜单"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={submitSearch} role="search" className="flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1">
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索爆料关键词"
              aria-label="搜索爆料关键词"
              className="flex-1 bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="flex h-8 items-center rounded-full bg-primary-500 px-3 text-xs font-medium text-background-50">
              搜索
            </button>
          </form>
          <nav aria-label="移动端导航" className="mt-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(link.to)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="mt-4 px-3 text-xs font-medium text-foreground-500">主题分类</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/topic/${cat.slug}`}
                className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-800"
              >
                <i className={`${cat.icon} text-base text-secondary-600`} />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}